import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@tabipla/shared";
import { profileKeys } from "./query-keys";
import type {
  AvatarInfoDto,
  UserResponseDto,
  UpdateAvatarInput,
  UpdateProfileInput,
  UseProfileCoreOptions,
} from "./types";
import { toUser } from "./types";

/**
 * プロフィールのコア機能を提供するフック
 *
 * 以下の機能を提供:
 * - アバター情報の取得・更新・画像アップロード・削除
 * - プロフィール更新
 * - アカウント削除
 *
 * @example
 * // 基本的な使用方法
 * const profile = useProfileCore({
 *   apiClient,
 *   getUserId: () => user?.id ?? null,
 *   getUser: () => user,
 *   setUser,
 * });
 *
 * // アプリ固有のクエリ無効化を追加
 * const profile = useProfileCore({
 *   apiClient,
 *   getUserId: () => user?.id ?? null,
 *   getUser: () => user,
 *   setUser,
 *   additionalInvalidateKeys: [["groups"]], // Waritto: グループ一覧も更新
 * });
 */
export function useProfileCore(options: UseProfileCoreOptions) {
  const { apiClient, getUserId, getUser, setUser, additionalInvalidateKeys = [] } = options;
  const queryClient = useQueryClient();
  const userId = getUserId();

  /**
   * アバター変更後にユーザー状態を更新するヘルパー
   */
  const updateUserWithAvatar = (avatarData: AvatarInfoDto) => {
    const currentUser = getUser();
    if (currentUser) {
      setUser({
        ...currentUser,
        image: avatarData.image ?? undefined,
        avatarEmoji: avatarData.avatarEmoji ?? undefined,
        avatarColor: avatarData.avatarColor ?? undefined,
      });
    }
  };

  /**
   * アバター情報を取得
   */
  const avatarInfo = useQuery({
    queryKey: profileKeys.avatarInfo(),
    queryFn: async () => {
      const response = await apiClient.get<AvatarInfoDto>("/api/users/avatar");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch avatar info");
      }
      return response.data!;
    },
    enabled: !!userId,
  });

  /**
   * アバター（絵文字・色）を更新
   */
  const updateAvatar = useMutation({
    mutationFn: async (input: UpdateAvatarInput) => {
      const response = await apiClient.put<AvatarInfoDto>("/api/users/avatar", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update avatar");
      }
      return response.data!;
    },
    onSuccess: (data) => {
      // ユーザー状態を更新（サイドバー等に即時反映）
      updateUserWithAvatar(data);
      queryClient.invalidateQueries({ queryKey: profileKeys.avatarInfo() });
      // アプリ固有のクエリも無効化
      for (const key of additionalInvalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  /**
   * アバター画像をアップロード
   */
  const uploadAvatarImage = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.uploadFormData<AvatarInfoDto>(
        "/api/users/avatar/image",
        formData
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to upload avatar image");
      }
      return response.data!;
    },
    onSuccess: (data) => {
      // ユーザー状態を更新（サイドバー等に即時反映）
      updateUserWithAvatar(data);
      queryClient.invalidateQueries({ queryKey: profileKeys.avatarInfo() });
      for (const key of additionalInvalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  /**
   * アバター画像を削除
   */
  const deleteAvatarImage = useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<AvatarInfoDto>("/api/users/avatar/image");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete avatar image");
      }
      return response.data!;
    },
    onSuccess: (data) => {
      // ユーザー状態を更新（サイドバー等に即時反映）
      updateUserWithAvatar(data);
      queryClient.invalidateQueries({ queryKey: profileKeys.avatarInfo() });
      for (const key of additionalInvalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  /**
   * プロフィール（名前）を更新
   */
  const updateProfile = useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      const currentUserId = getUserId();
      if (!currentUserId) {
        throw new Error("User not found");
      }

      const response = await apiClient.put<UserResponseDto>(`/api/users/${currentUserId}`, input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update profile");
      }

      return toUser(response.data!);
    },
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });

  /**
   * アカウントを削除
   */
  const deleteAccount = useMutation({
    mutationFn: async () => {
      const currentUserId = getUserId();
      if (!currentUserId) {
        throw new Error("User not found");
      }

      const response = await apiClient.delete(`/api/users/${currentUserId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete account");
      }
    },
    onSuccess: () => {
      setUser(null);
    },
  });

  return {
    avatarInfo,
    updateAvatar,
    uploadAvatarImage,
    deleteAvatarImage,
    updateProfile,
    deleteAccount,
  };
}
